import { AppError } from '@app/errors/AppError'

import { createSelector } from '@app/utils/createSelector'
import { urlEscaped } from '@app/utils/urlEscaped'

import { unwrapOr } from '@app/packages/Result/Result'
import type { ThunkSelector } from '@app/packages/thunkSelector/thunkSelector'
import { createLoadable, createStaticLoadableSelector, unwrapLoadableState } from '@app/packages/thunkSelector/thunkSelector'

import { userMetaTagsSelector } from '@app/store/slices/user_meta_tags'
import { usersModelsSelector as usersSelector } from '@app/store/slices/users'

/** Returns list of users */
export const usersListSelector = createSelector([usersSelector], userMap => Object.values(userMap))

export const makeUserByIdSelector = (userIdSelector: (state: unknown, props?: any) => string | null) =>
  createSelector([usersSelector, userIdSelector], (users, userId) => (!userId ? null : users[userId] || null))

export const makeParentByIdSelector = (userIdSelector: (state: unknown, props?: any) => string | null) =>
  createSelector([usersSelector, userIdSelector], (users, userId) => {
    if (!userId) return null
    const user = users[userId]
    if (!user) return null
    if (user.account_type !== 'parent') return null
    return user
  })

export const makeUsersByIdSelector = (userIdsSelector: (state: unknown, props?: any) => string[]) =>
  createSelector([usersSelector, userIdsSelector], (users, ids) =>
    ids
      .map(id => {
        const user = users[id]
        if (!user && BUILD_MODE === 'dev') console.error(`user with id ${id} not found`)
        return user
      })
      .filter(u => !!u)
  )

export const createUserResultSelector = (idSelector: ThunkSelector<string>) =>
  createSelector([usersSelector, idSelector], (users, idResult) =>
    createLoadable(c => {
      const userId = c.unwrap(idResult)
      const user = users[userId]
      if (!user) throw new AppError(`User with id ${userId} is missing`).withExpose(true)
      return user
    })
  )

export const createSitterResultSelector = (idSelector: ThunkSelector<string>) =>
  createSelector([createUserResultSelector(idSelector)], userResult =>
    createLoadable(c => {
      const user = c.unwrap(userResult)
      if (user.account_type !== 'sitter') throw new Error('User is not sitter')
      return user
    })
  )

/** @deprecated use createSitterResultSelector */
export const createSitterSelector = (id: string) => {
  return createSelector([createSitterResultSelector(createStaticLoadableSelector(id))], sitterResult => unwrapOr(sitterResult, null))
}

export const createSitterMetaTagsResultSelector = (idSelector: ThunkSelector<string>) =>
  createSelector([userMetaTagsSelector, idSelector], (meta, sitterIdResult) =>
    createLoadable(c => {
      const sitterId = c.unwrap(sitterIdResult)
      const tags = meta[sitterId] ? meta[sitterId] : null
      if (!tags) throw new Error('Meta tags are missing')
      return tags
    })
  )

export const createUserURLResultSelector = (idSelector: ThunkSelector<string>) => {
  const userSelector = createUserResultSelector(idSelector)

  return createSelector([userSelector], userResult =>
    createLoadable(c => {
      const user = c.unwrap(userResult)
      if (user.account_type === 'sitter') {
        return urlEscaped`/s/${user.token}`
      }
      if (user.account_type === 'parent') {
        return urlEscaped`/p/${user.token}`
      }
      throw new Error('Unsopported account type')
    })
  )
}

/** @deprecated use createUserURLResultSelector */
export const createUserURLSelector = (id: string) => {
  return createSelector([createUserURLResultSelector(createStaticLoadableSelector(id))], urlResult => {
    if (urlResult.error) return ''
    return unwrapLoadableState(urlResult.value) ?? ''
  })
}
